import { useState } from "react";
import { useIntl } from "react-intl";
import { capitalize } from "src/utils/capitalize";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import Plus from "src/design-system/icons/Plus.svg?react";
import Minus from "src/design-system/icons/Minus.svg?react";
import { tw } from "src/design-system/tw";
import type {
  Passenger,
  PassengerDetails,
  PassengerType,
} from "../../../../../PassengerDetailsProvider";
import { uuid } from "../../../../../utils/uuid";
import messages from "../PassengerPicker.message";
import { getPassengerDetailsErrors } from "../PassengerDetailsValidation";
import { AgeSelect } from "./AgeSelect";

const MIN_AGES = {
  youth: 0,
  senior: 58,
};
const MAX_AGES = {
  youth: 26,
  senior: 118,
};

type PassengerPickerFormProps = {
  passengers: PassengerDetails;
  onChange(passengers: PassengerDetails): void;
};

export function PassengerPickerForm({
  passengers,
  onChange,
}: PassengerPickerFormProps) {
  const intl = useIntl();
  const [maxPassengersError, setMaxPassengersError] = useState(false);

  const errorMessages = getPassengerDetailsErrors(passengers);
  if (maxPassengersError) {
    errorMessages.push(messages.maxPassengersMessage);
  }

  function minusButtonClick(passengerType: PassengerType) {
    if (maxPassengersError) {
      setMaxPassengersError(false);
    }

    sendAnalyticsInteractionEvent({
      category: "SegmentPassengerPicker",
      action: `Click:Remove${capitalize(passengerType)}`,
    });

    // Equivalent to findLastIndex, which is unavailable on some browsers
    const skipIndex = passengers.reduce(
      (prev, passenger, index) =>
        passenger.type === passengerType ? index : prev,
      -1,
    );
    if (skipIndex >= 0) {
      onChange([
        ...passengers.slice(0, skipIndex),
        ...passengers.slice(skipIndex + 1, passengers.length),
      ]);
    }
  }

  function plusButtonClick(passengerType: PassengerType) {
    if (passengers.length === 9) {
      setMaxPassengersError(true);
      return;
    }

    sendAnalyticsInteractionEvent({
      category: "SegmentPassengerPicker",
      action: `Click:Add${capitalize(passengerType)}`,
    });
    onChange([...passengers, { type: passengerType, age: "", id: uuid() }]);
  }

  function onPassengerChange(changedPassenger: Passenger) {
    onChange(
      passengers.map((passenger) =>
        passenger.id === changedPassenger.id ? changedPassenger : passenger,
      ),
    );
  }

  function getPassengersByType(passengerType: string) {
    return passengers.filter((passenger) => passenger.type === passengerType);
  }

  return (
    <>
      <div className="mt-2 mr-1 mb-0 ml-1 flex min-h-[60vh] w-full flex-col lg:min-h-0 lg:pb-3">
        {(["adult", "youth", "senior"] as const).map((passengerType) => (
          <div
            className="flex flex-col content-between border-b-1 border-b-border-secondary lg:last:border-b-0"
            key={passengerType}
          >
            <PassengerPickerRow
              passengerType={passengerType}
              passengers={getPassengersByType(passengerType)}
              onRemove={
                // Can't remove passengers if there's only 1
                passengers.length > 1
                  ? () => minusButtonClick(passengerType)
                  : undefined
              }
              onAdd={
                // Can't add passengers if there's already 9
                maxPassengersError
                  ? undefined
                  : () => plusButtonClick(passengerType)
              }
            />
            {passengerType !== "adult" && (
              <PassengerAgeSelectors
                passengerType={passengerType}
                passengers={getPassengersByType(passengerType)}
                onPassengerChange={onPassengerChange}
              />
            )}
          </div>
        ))}
        {/* TODO: These should be individual tooltips on the relevant components */}
        {errorMessages.length > 0 && (
          <div className="flex items-center bg-surface-caution p-3">
            {errorMessages.length > 0 &&
              intl.formatMessage(errorMessages[errorMessages.length - 1])}
          </div>
        )}
      </div>
    </>
  );
}

type PassengerPickerRowProps = {
  passengerType: PassengerType;
  passengers: PassengerDetails;
  onRemove?: () => void;
  onAdd?: () => void;
};

function PassengerPickerRow({
  passengerType,
  passengers,
  onRemove,
  onAdd,
}: PassengerPickerRowProps) {
  const intl = useIntl();

  return (
    <div className="flex flex-row justify-between py-3">
      <div>
        <h4 className="leading-tight">
          {intl.formatMessage(messages[passengerType])}
        </h4>
        <span className="text-sm text-text-secondary">
          {intl.formatMessage(messages[`${passengerType}AgeRange`])}
        </span>
      </div>
      <div className="flex flex-row items-center gap-5">
        <button
          className={tw(
            "cursor-pointer rounded-full border-1 border-border-secondary bg-white p-2 focus:outline-2 focus:outline-ring",
            (onRemove === undefined || passengers.length === 0) &&
              "cursor-not-allowed opacity-50",
          )}
          onClick={onRemove}
          title={intl.formatMessage(messages[`${passengerType}RemoveLabel`])}
          disabled={!onRemove || passengers.length === 0}
        >
          <Minus />
        </button>
        <div>{passengers.length}</div>
        <button
          className={tw(
            "cursor-pointer rounded-full border-1 border-border-secondary bg-white p-2 focus:outline-2 focus:outline-ring",
            onAdd === undefined && "cursor-not-allowed opacity-50",
          )}
          onClick={onAdd}
          title={intl.formatMessage(messages[`${passengerType}AddLabel`])}
          disabled={onAdd === undefined}
        >
          <Plus />
        </button>
      </div>
    </div>
  );
}

type PassengerAgeSelectorsProps = {
  passengerType: "senior" | "youth";
  passengers: PassengerDetails;
  onPassengerChange(passenger: Passenger): void;
};

function PassengerAgeSelectors({
  passengerType,
  passengers,
  onPassengerChange,
}: PassengerAgeSelectorsProps) {
  const intl = useIntl();

  return (
    <div className="flex flex-col">
      {passengers.map((passenger, index) => (
        <div
          className="flex items-center justify-between border-t-1 border-t-neutral-600 p-3"
          key={passenger.id}
        >
          <span>
            {intl.formatMessage(messages[`${passengerType}PassengerLabel`], {
              passengerNum: index + 1,
            })}
          </span>

          <AgeSelect
            age={passenger.age}
            onChange={(age) => onPassengerChange({ ...passenger, age })}
            max={MAX_AGES[passengerType]}
            min={MIN_AGES[passengerType]}
          />
        </div>
      ))}
    </div>
  );
}
